import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSnackbar } from '../../providers/SnackbarProvider';
import { useTranslation } from 'react-i18next';
// import { useMoveCompanyToAnotherSharedDriveMutation } from '../../gql/generated/graphql';

interface MoveCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName?: string;
  currentDrive?: string;
}

const SHARED_DRIVE_OPTIONS = [
  { value: 'SOURCING', label: 'Sourcing' },
  { value: 'EXECUTION', label: 'Execution' },
  { value: 'PORTFOLIO_FUND_1', label: 'Portfolio Fund 1' },
  { value: 'PORTFOLIO_FUND_2', label: 'Portfolio Fund 2' },
  { value: 'PORTFOLIO_FUND_3', label: 'Portfolio Fund 3' },
  { value: 'PORTFOLIO_CV_1', label: 'Portfolio CV 1' },
];

const MoveCompanyDialog: React.FC<MoveCompanyDialogProps> = ({
  open,
  onClose,
  companyId,
  companyName,
  currentDrive,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [selectedDrive, setSelectedDrive] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);

  const handleDriveChange = (event: SelectChangeEvent<string>) => {
    setSelectedDrive(event.target.value);
  };

  const handleMove = async () => {
    if (!selectedDrive) {
      showSnackbar('Please select a destination shared drive', 'warning');
      return;
    }

    if (selectedDrive === currentDrive) {
      showSnackbar('Company is already in the selected shared drive', 'info');
      return;
    }

    setIsMoving(true);

    try {
      // TODO: Uncomment when the GraphQL mutation is available
      // const { data } = await moveCompany({
      //   variables: {
      //     companyId,
      //     newSharedDriveName: selectedDrive,
      //   },
      // });

      // if (data?.moveCompanyToAnotherSharedDrive?.success) {
      //   showSnackbar(
      //     data.moveCompanyToAnotherSharedDrive.message ||
      //     `Successfully moved ${companyName || 'company'} to ${selectedDrive}`,
      //     'success'
      //   );
      //   onClose();
      // } else {
      //   throw new Error(data?.moveCompanyToAnotherSharedDrive?.message || 'Move failed');
      // }

      // Temporary mock success for development
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      showSnackbar(
        `Successfully moved ${companyName || 'company'} to ${SHARED_DRIVE_OPTIONS.find(opt => opt.value === selectedDrive)?.label}`,
        'success'
      );
      onClose();
    } catch (error) {
      console.error('Error moving company:', error);
      showSnackbar(`Failed to move company: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    if (!isMoving) {
      setSelectedDrive('');
      onClose();
    }
  };

  const selectedDriveLabel = SHARED_DRIVE_OPTIONS.find(opt => opt.value === selectedDrive)?.label;
  const currentDriveLabel = SHARED_DRIVE_OPTIONS.find(opt => opt.value === currentDrive)?.label;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isMoving}>
      <DialogTitle>{t('assessment.moveCompanyToAnotherSharedDriveDialogTitle')}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Company:</strong> {companyName || `Company ID ${companyId}`}
          </Typography>

          {currentDrive && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>{t('assessment.moveCompanyToAnotherSharedDriveDialogCurrentDrive')}:</strong>{' '}
              {currentDriveLabel || currentDrive}
            </Typography>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {t('assessment.moveCompanyToAnotherSharedDriveDialogInfo')}
        </Alert>

        <FormControl fullWidth>
          <InputLabel id="shared-drive-select-label">
            {t('assessment.moveCompanyToAnotherSharedDriveDialogNewSharedDriveName')}
          </InputLabel>
          <Select
            labelId="shared-drive-select-label"
            value={selectedDrive}
            label={t('assessment.moveCompanyToAnotherSharedDriveDialogNewSharedDriveName')}
            onChange={handleDriveChange}
            disabled={isMoving}
          >
            {SHARED_DRIVE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value} disabled={option.value === currentDrive}>
                {option.label}
                {option.value === currentDrive && ' (Current)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedDrive && selectedDrive !== currentDrive && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('assessment.moveCompanyToAnotherSharedDriveDialogWarning', { selectedDriveLabel })}
            {t('assessment.moveCompanyToAnotherSharedDriveDialogInfoCopyFiles')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isMoving}>
          Cancel
        </Button>
        <Button
          onClick={handleMove}
          variant="contained"
          disabled={!selectedDrive || selectedDrive === currentDrive || isMoving}
          startIcon={isMoving ? <CircularProgress size={16} /> : null}
        >
          {isMoving ? 'Moving...' : 'Move Company'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveCompanyDialog;
